/** Connected, physical wall-top movement with one reserved post per model. */

import { MAP_H, MAP_W } from '../constants.ts';
import { alliedWith, UF, UnitState, WallKind } from '../types.ts';
import { LogTone, pushLog } from '../events.ts';
import { companyCommanded, findCompany, tileBlocks, OrderKind, Stance, type Company, type World } from '../world.ts';
import { unitType } from '../../data/units.ts';
import { highGroundRange } from '../field.ts';
import { planCompanyRoute } from './companies.ts';
import {
  climbable,
  curtainRun,
  curtainRoute,
  insideFoot,
  isCurtain,
  nearestCurtain,
  tileCapacity,
} from './curtain.ts';

/**
 * What height is worth, in tiles of extra reach, to something that shoots.
 *
 * Melee gets none of it, and that is the design rather than an oversight. A
 * spearman on a parapet cannot reach a man standing at the foot of it — but
 * nothing at the foot of it can reach him either, so what he is *for* is the
 * ladder. Archers shoot further; spears hold the top of the ladder. Put both on
 * the wall and you have a wall.
 */
export const WALL_RANGE_BONUS = 2.4;
export const TOWER_RANGE_BONUS = 4.2;

/**
 * A missile that has to go *up* is a missile that mostly hits stone.
 *
 * Only arrows and bolts — a boulder from a trebuchet does not care about a
 * crenellation, and neither does fire. This is what makes manning the wall the
 * answer to a horde of archers rather than a way to lose your bowmen faster.
 */
export const PARAPET_COVER = 0.55;

/** Anything that reaches further than a long spear can fight upward unaided. */
export const CLIMB_FREE_RANGE = 2.2;

/** What a fall off a broken section costs, as a fraction of maximum health. */
export const FALL_DAMAGE = 0.22;
export const FALL_STUN = 1.1;

/**
 * How often posts are re-assigned, in ticks.
 *
 * Assignment is a walk of the curtain run and of every garrisoned company's
 * roster, which is far too much to do thirty times a second and far more often
 * than anything can possibly change. Every ten ticks is a third of a second —
 * quick enough that a man knocked off a collapsing section is back up on the
 * next stone before the dust settles, cheap enough to be invisible on the
 * profile. A fixed stride off `w.tick` rather than a timer, so it is the same
 * tick in every replay.
 */
const GARRISON_STRIDE = 10;

/** Decode the occupied/next deck tile; values below -1 mean walking down from it. */
export function wallWaypoint(w: World, i: number): number {
  const at = w.units.wallAt[i];
  return at < -1 ? -at - 2 : at;
}

/** Find a real ground tile beside this deck, walking the component if necessary. */
function deckAccess(w: World, tile: number, i: number): { tile: number; x: number; y: number } | null {
  const probe = (k: number) => {
    const foot = insideFoot(w.map, k, (x, y) => !tileBlocks(w, x, y, i));
    const x = Math.floor(foot.x), y = Math.floor(foot.y);
    if (!w.map.inBounds(x, y) || isCurtain(w.map, w.map.idx(x, y)) || tileBlocks(w, x, y, i)) return null;
    return { tile: k, ...foot };
  };
  // A tower's back row uses the same inside stair as its front row. Only the
  // small connected tower block is inspected, never the whole curtain.
  if (w.map.wall[tile] === WallKind.Tower) {
    const block = [tile];
    let best: ReturnType<typeof probe> = null;
    let score = Infinity;
    for (let n = 0; n < block.length; n++) {
      const k = block[n], x = k % MAP_W, y = Math.floor(k / MAP_W);
      const candidate = probe(k);
      if (candidate) {
        const d = (candidate.x - w.map.hearthX) ** 2 + (candidate.y - w.map.hearthY) ** 2;
        if (d < score || (d === score && best !== null && candidate.tile < best.tile)) { best = candidate; score = d; }
      }
      for (const [dx, dy] of [[0, -1], [1, 0], [0, 1], [-1, 0]]) {
        if (block.length >= 16 || !w.map.inBounds(x + dx, y + dy)) continue;
        const next = w.map.idx(x + dx, y + dy);
        if (w.map.wall[next] === WallKind.Tower && !block.includes(next)) block.push(next);
      }
    }
    if (best) return best;
  }
  const own = probe(tile);
  if (own) return own;
  for (const k of curtainRoute(w.map, tile).keys()) {
    const found = probe(k);
    if (found) return found;
  }
  return null;
}

/**
 * A single waypoint for movement. wallTile is the reservation, wallAt is the
 * current walking leg. Both survive saves; the bounded deck graph is derived.
 */
export function wallMoveTarget(w: World, i: number, out: { x: number; y: number }): boolean {
  const u = w.units;
  if (u.wallTile[i] < 0 && u.wallAt[i] === -1) return false;
  if (!alliedWith(u.team[i], w.wallTeam)) { u.wallTile[i] = -1; u.wallAt[i] = -1; return false; }
  if (u.wallTile[i] >= 0 && !isCurtain(w.map, u.wallTile[i])) u.wallTile[i] = -1;
  const rawAt = u.wallAt[i];
  let at = wallWaypoint(w, i);
  const actual = Math.floor(u.y[i]) * MAP_W + Math.floor(u.x[i]);
  const on = standingOn(w, i);
  const centre = (k: number) => { out.x = k % MAP_W + 0.5; out.y = Math.floor(k / MAP_W) + 0.5; };
  if (at >= 0 && !isCurtain(w.map, at)) {
    u.wallAt[i] = on >= 0 ? on : -1;
    at = on;
  }
  if (rawAt < -1) {
    if (on < 0) { u.wallAt[i] = -1; return u.wallTile[i] >= 0 ? wallMoveTarget(w, i, out) : false; }
    const exit = deckAccess(w, on, i);
    if (!exit) { centre(on); return true; }
    out.x = exit.x; out.y = exit.y;
    return true;
  }
  if (on >= 0) {
    // Finish the current leg before turning: no diagonal cuts across corners.
    if (at >= 0 && isCurtain(w.map, at)) {
      centre(at);
      if (Math.hypot(out.x - u.x[i], out.y - u.y[i]) > 0.07) return true;
    }
    const post = u.wallTile[i];
    if (post === actual) { u.wallAt[i] = post; centre(post); return true; }
    const next = post >= 0 ? curtainRoute(w.map, post).get(actual) : undefined;
    if (next !== undefined) {
      u.wallAt[i] = next; centre(next); return true;
    }
    // Released or disconnected: walk to a nearby exit, then approach on ground.
    const exit = deckAccess(w, actual, i);
    if (!exit) { centre(actual); return true; }
    if (exit.tile !== actual) {
      const leg = curtainRoute(w.map, exit.tile).get(actual);
      if (leg !== undefined) { u.wallAt[i] = leg; centre(leg); return true; }
      centre(actual); return true;
    }
    u.wallAt[i] = -actual - 2;
    out.x = exit.x; out.y = exit.y; return true;
  }
  if (u.wallTile[i] < 0) { u.wallAt[i] = -1; return false; }
  const entry = deckAccess(w, u.wallTile[i], i);
  if (!entry) { u.wallAt[i] = -1; out.x = u.x[i]; out.y = u.y[i]; return true; }
  u.wallAt[i] = entry.tile;
  const footDistance = Math.hypot(entry.x - u.x[i], entry.y - u.y[i]);
  if (footDistance < 0.68) { centre(entry.tile); return true; }
  out.x = entry.x; out.y = entry.y;
  const company = findCompany(w, u.companyId[i]);
  // Use the existing company route for the long approach around city buildings.
  if (company && company.route.length > 0 && footDistance > 3) {
    out.x = company.ax; out.y = company.ay;
  }
  return true;
}

/** The post this model has been given, or −1. */
export function postOf(w: World, i: number): number {
  return w.units.wallTile[i];
}

/**
 * The section this model is *actually standing on*, or −1.
 *
 * Derived from its position rather than stored, because "assigned" and "up
 * there" are different states and only one of them is worth range and cover. A
 * man crossing the last two tiles to his post is on the ground, is reachable,
 * and shoots like anyone else. Two floors and a compare; called from the hot
 * paths, so it takes the cheap exit first.
 */
export function standingOn(w: World, i: number): number {
  if (w.units.wallTile[i] < 0 && w.units.wallAt[i] === -1) return -1;
  if (!alliedWith(w.units.team[i], w.wallTeam)) return -1;
  const tx = Math.floor(w.units.x[i]);
  const ty = Math.floor(w.units.y[i]);
  if (tx < 0 || ty < 0 || tx >= MAP_W || ty >= MAP_H) return -1;
  const k = ty * MAP_W + tx;
  if (!isCurtain(w.map, k)) return -1;
  const at = wallWaypoint(w, i);
  return k === w.units.wallTile[i] || k === at || (at >= 0 && Math.abs(tx - at % MAP_W) + Math.abs(ty - Math.floor(at / MAP_W)) === 1) ? k : -1;
}

/**
 * Extra reach from height, for something that shoots. Zero for everybody else.
 *
 * Two kinds of height now, and the parapet is only one of them. A man on a
 * ridge sees further than a man in the bottom for exactly the reason a man on a
 * wall does, and a battlefield that hands out the high ground without paying
 * for it in reach has hidden the only thing the high ground is for. The ground
 * term is worth less than a wall and much less than a tower — see
 * `HIGH_GROUND_RANGE_CAP` in `field.ts`; a knoll is not a keep — and it is
 * zero on every world with no relief, which is every settlement.
 *
 * They add rather than compete: a tower built on a rise is a tower on a rise.
 */
export function elevationRange(w: World, i: number): number {
  const ground = highGroundRange(w, i);
  if (w.units.wallTile[i] < 0 && w.units.wallAt[i] === -1) return ground;
  const k = standingOn(w, i);
  if (k < 0) return ground;
  return ground + (w.map.wall[k] === WallKind.Tower ? TOWER_RANGE_BONUS : WALL_RANGE_BONUS);
}

/** True if this model is up on the curtain right now. */
export function elevated(w: World, i: number): boolean {
  return standingOn(w, i) >= 0;
}

/**
 * Can `attacker` get at `defender` at all?
 *
 * This is the rule the whole siege hangs off. A man on an intact curtain cannot
 * be reached from the ground: not by a grunt, not by a wolf, not by an ogre. He
 * can be reached by something that flies, by something that shoots, by a sapper
 * or an engine — the ladders and the towers, which is what finally makes
 * `UF.Sapper` and `UF.Siege` mean something — and at a gate or beside a hole in
 * the line, where the wall has stopped being a wall.
 *
 * Everything that fails this test falls through to `pickStructureTarget` and
 * starts hitting the stone instead, which is exactly what a besieger with no
 * ladders should do.
 *
 * `climberFlags` and `attackerReach` are hoisted by the caller: the targeting
 * sweep asks this once per candidate and the answer's attacker half does not
 * change between candidates.
 */
export function reachableFromGround(
  w: World,
  defender: number,
  climber: boolean,
  attackerReach: number,
): boolean {
  if (w.units.wallTile[defender] < 0 && w.units.wallAt[defender] === -1) return true;
  const k = standingOn(w, defender);
  if (k < 0) return true;
  if (climber) return true;
  if (attackerReach >= CLIMB_FREE_RANGE) return true;
  return climbable(w.map, k);
}

/** Wings, ladders and engines. Resolved once per attacker, not once per target. */
export function isClimber(flags: number): boolean {
  return (flags & (UF.Flying | UF.Sapper | UF.Siege)) !== 0;
}

// ── Assignment ─────────────────────────────────────────────────────────────

/** Scratch for the per-tile occupancy count. Rebuilt from `wallTile`, never stored. */
const occupancy = new Map<number, number>();

function buildOccupancy(w: World): void {
  occupancy.clear();
  const u = w.units;
  for (const c of w.companies) {
    if (c.garrisonTile < 0) continue;
    for (const h of c.members) {
      const i = u.resolve(h);
      if (i < 0) continue;
      const k = u.wallTile[i];
      if (k < 0) continue;
      occupancy.set(k, (occupancy.get(k) ?? 0) + 1);
    }
  }
}

/**
 * Give every unassigned member of a garrisoned company a stone to stand on.
 *
 * Members are walked in roster order and tiles in the run's breadth-first order,
 * so the assignment is a pure function of the world — the same men end up on the
 * same stones in a replay. A company larger than the section it was pointed at
 * simply leaves its tail on the ground behind the wall, which is the honest
 * answer: the wall is only so long.
 */
export function assignPosts(w: World, c: Company): void {
  if (c.garrisonTile < 0 || !alliedWith(c.team, w.wallTeam)) return;
  const u = w.units;

  let wanting = 0;
  for (const h of c.members) {
    const i = u.resolve(h);
    if (i >= 0 && u.wallTile[i] < 0 && u.state[i] !== UnitState.Routing) wanting++;
  }
  if (wanting === 0) return;

  buildOccupancy(w);
  // Ask for enough curtain to hold the whole company at one man a tile, so a
  // run of towers does not stop the walk early and leave the flanks bare.
  const run = curtainRun(w.map, c.garrisonTile, Math.max(8, c.maxSize * 2));
  if (run.length === 0) return;

  let cursor = 0;
  for (const h of c.members) {
    const i = u.resolve(h);
    if (i < 0) continue;
    if (u.wallTile[i] >= 0) continue;
    if (u.state[i] === UnitState.Routing) continue;
    while (cursor < run.length) {
      const k = run[cursor];
      const cap = tileCapacity(w.map.wall[k]);
      const have = occupancy.get(k) ?? 0;
      if (have < cap) {
        occupancy.set(k, have + 1);
        u.wallTile[i] = k;
        u.goalX[i] = (k % MAP_W) + 0.5;
        u.goalY[i] = ((k / MAP_W) | 0) + 0.5;
        // A man told to take the wall stops chewing on whatever he was chewing.
        u.targetBuilding[i] = -1;
        u.targetWall[i] = -1;
        u.targetH[i] = -1;
        break;
      }
      cursor++;
    }
    if (cursor >= run.length) break;
  }
}

/**
 * Order a company onto the curtain.
 *
 * The tile clicked need not be a wall — anything within a few tiles of one
 * counts, because a player pointing at their own wall from a zoomed-out camera
 * is pointing at roughly the right stone and should not be told off for it.
 */
export function garrisonCompany(w: World, c: Company, tx: number, ty: number): boolean {
  if (!companyCommanded(w, c) || !alliedWith(c.team, w.wallTeam) || c.dead) return false;
  if (!w.map.inBounds(tx, ty)) return false;
  let k = w.map.idx(tx, ty);
  if (!isCurtain(w.map, k)) {
    k = nearestCurtain(w.map, tx, ty, 3);
    if (k < 0) return false;
  }
  // Reposting releases old reservations but preserves the actual elevated leg.
  for (const h of c.members) {
    const i = w.units.resolve(h);
    if (i < 0) continue;
    w.units.wallAt[i] = standingOn(w, i);
    w.units.wallTile[i] = -1;
  }
  c.garrisonTile = k;
  // Hold, not Guard: a company that has been put on a wall should not leave it
  // to chase somebody across the field. What it may shoot at is decided by the
  // parapet leash in `combat.ts`, which is centred on the man's own stone.
  if (c.stance === Stance.Pursue) c.stance = Stance.Guard;
  const first = c.members.map((h) => w.units.resolve(h)).find((i) => i >= 0);
  const foot = first === undefined ? insideFoot(w.map, k) : deckAccess(w, k, first) ?? insideFoot(w.map, k);
  c.orderX = foot.x;
  c.orderY = foot.y;
  c.orderKind = OrderKind.Move;
  planCompanyRoute(w, c);
  assignPosts(w, c);
  return true;
}

/** Come down off the wall. Posts are released; the company is a company again. */
export function standDown(w: World, c: Company): void {
  c.garrisonTile = -1;
  const u = w.units;
  for (const h of c.members) {
    const i = u.resolve(h);
    if (i >= 0) {
      u.wallAt[i] = standingOn(w, i);
      u.wallTile[i] = -1;
    }
  }
}

/**
 * Throw everybody standing on a section off it, and say who fell.
 *
 * Called from `damageWall` the instant a section collapses. The damage is
 * applied by the caller rather than here, because killing a unit lives in
 * `combat.ts` and a garrison module that could kill people would be a circular
 * import as well as a surprise.
 */
export function unmountTile(w: World, tile: number, out: number[]): void {
  out.length = 0;
  const u = w.units;
  for (let i = 0; i < u.slots.high; i++) {
    if (u.slots.alive[i] === 0) continue;
    const occupied = Math.floor(u.y[i]) * MAP_W + Math.floor(u.x[i]);
    if (occupied === tile && (u.wallTile[i] >= 0 || u.wallAt[i] !== -1)) {
      u.wallTile[i] = -1; u.wallAt[i] = -1; out.push(i);
    } else {
      if (u.wallTile[i] === tile) u.wallTile[i] = -1;
      if (wallWaypoint(w, i) === tile) u.wallAt[i] = isCurtain(w.map, occupied) ? occupied : -1;
    }
  }
}

/**
 * Keep the garrisons honest: replace the fallen, slide a post whose stone is
 * gone, and stand a company down when the wall it was holding no longer exists.
 */
export function stepGarrison(w: World): void {
  if (w.tick % GARRISON_STRIDE !== 0) return;
  const u = w.units;

  for (const c of w.companies) {
    if (c.garrisonTile < 0) continue;
    if (!alliedWith(c.team, w.wallTeam)) { standDown(w, c); continue; }
    if (c.dead || c.members.length === 0) {
      c.garrisonTile = -1;
      continue;
    }

    // A post whose stone has gone is not a post. This is belt and braces on top
    // of `unmountTile` — a section can also be demolished, or swallowed by a
    // tower going up on top of it.
    for (const h of c.members) {
      const i = u.resolve(h);
      if (i < 0) continue;
      const k = u.wallTile[i];
      if (k >= 0 && !isCurtain(w.map, k)) u.wallTile[i] = -1;
    }

    if (!isCurtain(w.map, c.garrisonTile)) {
      const x = c.garrisonTile % MAP_W;
      const y = (c.garrisonTile / MAP_W) | 0;
      const slid = nearestCurtain(w.map, x, y, 5);
      if (slid < 0) {
        standDown(w, c);
        pushLog(w.events, `${c.name} has no wall left to hold.`, LogTone.Bad);
        continue;
      }
      c.garrisonTile = slid;
    }

    assignPosts(w, c);
  }
}

/**
 * Ranged troops on a wall want to be pointed at what they can actually hit, and
 * that is a different question from where their company's banner is. Used by the
 * stance leash: a man on a parapet is tethered to his own stone, not to an
 * anchor that may be a hundred tiles behind him.
 */
export function parapetLeashRadius(w: World, i: number): number {
  const t = unitType(w.units.typeId[i]);
  return Math.max(3, t.range + elevationRange(w, i) + 2.5);
}
